import moment from "moment-timezone";
import { browserTimezone } from "@/lib/helpers.js";

// Update moment locale to start weeks on mon instead of sunday
moment.updateLocale("en", {
  week: {
    dow: 1
  }
});

// NOTE: when using startOf or endOf, the toDate will auto adjust based on the custom timezone
// We should convert to the users actual timezone before using toDate() on these methods.

export const buildCurrentRange = (period) => {
  const start = moment().tz(browserTimezone).startOf(period).toDate();
  const end = moment().tz(browserTimezone).endOf(period).toDate();
  return [start, end];
};

export const buildFutureRange = (period, offset = [1, 1]) => {
  const start = moment()
    .tz(browserTimezone)
    .add(offset[0], period)
    .startOf(period)
    .toDate();
  const end = moment()
    .tz(browserTimezone)
    .add(offset[1], period)
    .endOf(period)
    .toDate();
  return [start, end];
};

export const buildPreviousRange = (period, offset = [1, 1]) => {
  const start = moment()
    .tz(browserTimezone)
    .subtract(offset[1], period)
    .startOf(period)
    .toDate();
  const end = moment()
    .tz(browserTimezone)
    .subtract(offset[0], period)
    .endOf(period)
    .toDate();
  return [start, end];
};

// Calendar-day conversions, for UI that deals in a day rather than an instant.
//
// `lib/helpers.js` sets moment's default zone to the tenant's, so converting a
// native Date through an instant lands on the wrong day whenever the admin's
// device sits in another zone. The builders above escape that by re-anchoring
// with `.tz(browserTimezone)`; these two escape it by never leaving the
// calendar components at all, which is what a date grid needs.

/**
 * Parse a stored date into a machine-local Date at midnight.
 *
 * ISO is tried first because that is what a picker stores once a value has been
 * applied, while the display format is what it renders -- both reach here, and
 * parsing an ISO string against `MM/DD/YY` silently yields the wrong day.
 * Mirrors the same precedence in CalendarDatepicker's own `formatDate`.
 */
export const toLocalDateOnly = (value, format) => {
  if (!value) return null;
  const parsed = moment(value, moment.ISO_8601, true).isValid()
    ? moment(value)
    : moment(value, format);
  return parsed.isValid()
    ? new Date(parsed.year(), parsed.month(), parsed.date())
    : null;
};

/** Format a Date by its own calendar components, ignoring any zone offset. */
export const formatLocalDateOnly = (date, format) =>
  date
    ? moment([date.getFullYear(), date.getMonth(), date.getDate()]).format(
        format
      )
    : "";

export const offsetWeek = (range) => {
  // add a +1 offset to weeks so they are mon-sun instead of sun-sat
  return range.map((date) =>
    moment(date).tz(browserTimezone).add(1, "day").toDate()
  );
};

export const previousYearRange = (range) => {
  return range.map((date) =>
    moment(date).tz(browserTimezone).subtract(1, "year").toDate()
  );
};

export const isFutureDate = (date) => moment().isBefore(date);
